import * as React from 'react';
import { connect } from 'react-redux';
import { FormikBag, FormikProps, useField, withFormik } from 'formik';
import {
  Dispatch,
  OBAddress,
  OBLogSourceAntiVirus,
  OBLogSourceDHCP,
  OBLogSourceDNS,
  OBLogSourceIDS,
  OBLogSourceProxy,
  OBSite,
  StrictSetFieldValue,
} from '../../../types';
import { onboardingSelector } from '../../../reducers/onboarding';
import { TIMEZONES } from '../../../constants';
import { apiRequest, APIRequestCallbacks } from '../../../APIClient';
import {
  Checkbox,
  MultiSelect,
  NumberInput,
  SingleSelect,
  TextArea,
  TextInput,
} from '../../../widgets/FormikInputFieldV2';
import { OnboardingModal } from '../OnboardingModal';
import { createSiteValidationSchema } from '../validationSchemas';
import { AddressFormInputs } from './forms/AddressFormInputs';
import './OnboardingSiteModal.scss';
import ipUtils from '../ipUtils';
import { Tooltip } from '../../../widgets/Tooltip';
import { Label } from 'reactstrap';

const FIREWALLS = [
  'Barracuda',
  'Checkpoint',
  'Cisco ASA',
  'Cisco FTD (Firepower)',
  'F5 WAF',
  'Cisco Meraki',
  'FireEye',
  'Forcepoint',
  'Fortinet',
  'Palo Alto Firewall',
  'Palo Alto Wildfire',
  'Sophos',
  'Ubiquiti Unifi',
  'Versa',
  'Sonic Wall',
  'Watchguard',
  'ZScaler ZIA (Legacy)',
  'ZScaler ZPA',
  'ZScaler ZIA',
  'Other',
];
const SWITCHES = [
  'Aruba',
  'Avaya',
  'Brocade',
  'Cisco Catalyst',
  'Cisco Nexus',
  'Cisco Meraki',
  'HP',
  'Juniper',
  'Ubiquiti',
  'Other',
];
const WIRELESSES = ['Aruba', 'Cisco', 'Cisco Meraki', 'Ubiquiti', 'Other'];

export const WINDOWS_VERSION_OPTIONS = [
  { value: '2003', label: '2003' },
  { value: '2003 R2', label: '2003 R2' },
  { value: '2008', label: '2008' },
  { value: '2008 R2', label: '2008 R2' },
  { value: '2012', label: '2012' },
  { value: '2012 R2', label: '2012 R2' },
  { value: '2016', label: '2016' },
  { value: '2019', label: '2019' },
];

const enumToOptions = (obj: { [key: string]: string }) =>
  Object.values(obj).map((value) => ({ label: value, value }));

const stringsToOptions = (arr: string[]) =>
  arr.map((item) => ({ label: item, value: item }));

// Static options, calculated only once
const LOG_SOURCE_DNS_OPTIONS = enumToOptions(OBLogSourceDNS);
const LOG_SOURCE_DHCP_OPTIONS = enumToOptions(OBLogSourceDHCP);
const FIREWALLS_OPTIONS = stringsToOptions(FIREWALLS);
const SWITCHES_OPTIONS = stringsToOptions(SWITCHES);
const WIRELESS_OPTIONS = stringsToOptions(WIRELESSES);
const LOG_SOURCE_ANTIVIRUS_OPTIONS = enumToOptions(OBLogSourceAntiVirus);
const LOG_SOURCE_IDS_OPTIONS = enumToOptions(OBLogSourceIDS);
const LOG_SOURCE_PROXY_OPTIONS = enumToOptions(OBLogSourceProxy);

type OwnProps = {
  isOpen: boolean;
  onClose: () => void;
  currentId: string;
};

export type StoreProps = {
  site: OBSite | undefined;
  siteNames: Set<string>;
};

export type DispatchProps = {
  onSave: (values: SiteFormValues, callbacks: APIRequestCallbacks) => void;
};

export type FormikOuterProps = OwnProps & DispatchProps & StoreProps;

export type Props = FormikOuterProps & FormikProps<SiteFormValues>;

export type SiteFormValues = Omit<OBSite, 'address'> &
  Omit<OBAddress, 'id'> & {
    addressId: string;
    noFirewalls: boolean;
    noDNS: boolean;
    noDHCP: boolean;
  };

export const EMPTY_SITE_FORM_DATA: SiteFormValues = {
  addressId: '',
  name: '',
  country: '',
  street: '',
  street2: '',
  city: '',
  region: '',
  postcode: '',
  timezone: '',
  firewalls: [],
  switches: [],
  wap: [],
  ipSubnets: '',
  networkBlacklist: '',
  scanningBlacklist: '',
  devices: '',
  adc: 0,
  noFirewalls: false,
  noDHCP: false,
  noDNS: false,
  // isolatedWireless: false,
  isADCDNS: false,
  isADCDHCP: false,
  thirdPartySite: false,
  oldestWindowsVersion: '',
  notes: '',
  finalized: null,

  iam: [],
  dns: [],
  dhcp: [],
  antivirus: [],
  mfa: '',
  ids: [],
  vpn: '',
  proxy: '',
  cls: '',
};

const OnboardingSiteModal: React.FunctionComponent<Props> = (props) => {
  const {
    isOpen,
    onClose,
    handleSubmit,
    isSubmitting,
    currentId,
    values,
    resetForm,
    isValid,
    setFieldTouched,
    dirty,
    site,
  } = props;

  const setFieldValue: StrictSetFieldValue<SiteFormValues> =
    props.setFieldValue;

  React.useEffect(() => {
    if (values.adc > 0) {
      setFieldValue('isADCDNS', true);
    } else {
      setFieldValue('isADCDNS', false);
      setFieldValue('oldestWindowsVersion', '');
      setFieldTouched('oldestWindowsVersion', false);
    }
    setFieldTouched('isADCDNS', false);
  }, [setFieldTouched, setFieldValue, values.adc]);

  React.useEffect(() => {
    if (values.noFirewalls) {
      setFieldValue('firewalls', []);
      setFieldTouched('firewalls', false);
    }
  }, [setFieldTouched, setFieldValue, values.noFirewalls]);

  React.useEffect(() => {
    if (values.noDNS) {
      setFieldValue('dns', []);
      setFieldTouched('dns', false);
    }
  }, [setFieldTouched, setFieldValue, values.noDNS]);

  React.useEffect(() => {
    if (values.noDHCP) {
      setFieldValue('dhcp', []);
      setFieldTouched('dhcp', false);
    }
  }, [setFieldTouched, setFieldValue, values.noDHCP]);

  const onCancel = React.useCallback(() => {
    onClose();
    resetForm();
  }, [onClose, resetForm]);

  const [
    ,
    // skip first item in tuple
    { error: nameError },
  ] = useField('name');

  let siteName: string;

  if (currentId) {
    if (nameError) {
      siteName = `Edit Site: ${site?.name}`;
    } else {
      siteName = `Edit Site: ${values?.name}`;
    }
  } else {
    if (nameError) {
      siteName = 'Add Site';
    } else {
      siteName = `Add Site: ${values?.name}`;
    }
  }

  return (
    <OnboardingModal
      title={siteName}
      visible={isOpen}
      onCancel={onCancel}
      onOk={handleSubmit}
      loading={isSubmitting}
      closable={!isSubmitting}
      okDisabled={!isValid}
      showConfirm
      isFormDirty={dirty}
    >
      <form className="onboarding-modal-form onboarding-site-form">
        <div className="d-flex">
          <div style={{ width: '400px' }}>
            <h5 className="mb-4">Site Information</h5>

            <div className="d-flex">
              <Label className="pb-1 mr-2 required">Site Name</Label>
              <Tooltip
                text="Choose a unique name to make this site easy to identify."
                placement="rightBottom"
              />
            </div>
            <TextInput name="name" required disabled={isSubmitting} />

            <AddressFormInputs disabled={isSubmitting} />

            <div className="d-flex">
              <Checkbox
                name="thirdPartySite"
                label="Third Party Site"
                className="pl-4 pr-2 pb-2"
                disabled={isSubmitting}
              />
              <Tooltip
                text="This site is managed by a third party (external data center, managed service provider, etc.)"
                placement="rightBottom"
              />
            </div>

            <SingleSelect
              name="timezone"
              id="selectTimezone"
              placeholder="Choose a time zone"
              label="Time Zone"
              options={TIMEZONES}
              className="mb-3"
              required
              isDisabled={isSubmitting}
            />

            <h5 className="mb-4">Site Configuration</h5>

            <NumberInput
              name="adc"
              label="Active Directory Domain Controllers"
              disabled={isSubmitting}
            />

            <Checkbox
              name="isADCDNS"
              label="DNS is running on a domain controller"
              disabled={values.adc <= 0 || !values.adc || isSubmitting}
              className="pl-4"
            />
            <Checkbox
              name="isADCDHCP"
              label="DHCP is running on a domain controller"
              disabled={values.adc <= 0 || !values.adc || isSubmitting}
              className="pl-4 pb-2"
            />

            <SingleSelect
              name="oldestWindowsVersion"
              id="selectOldestWindowsVersion"
              placeholder="Choose a version"
              label="Oldest version of Windows Server used"
              options={WINDOWS_VERSION_OPTIONS}
              className="mb-3"
              isDisabled={values.adc <= 0 || !values.adc || isSubmitting}
            />

            <TextArea
              name="ipSubnets"
              className="mt-1"
              label="IP Subnets"
              warningFunc={ipUtils.isIPPrivate}
              disabled={isSubmitting}
            />
            <input name="networkBlacklist" type="hidden" />
            <TextArea
              name="scanningBlacklist"
              className="mt-1"
              label="Networks to avoid NMAP scanning"
              disabled={isSubmitting}
            />
            <TextArea
              name="devices"
              className="mt-1"
              label="Existing Network Scanning/Monitoring Devices"
              disabled={isSubmitting}
            />
          </div>
          <div style={{ minWidth: '400px', marginLeft: '100px' }}>
            <h5 className="mb-4">Site Log Sources</h5>

            <MultiSelect
              name="dns"
              id="selectDns"
              placeholder="Choose a DNS"
              options={LOG_SOURCE_DNS_OPTIONS}
              label="DNS"
              isDisabled={values.noDNS || isSubmitting}
              required
            />

            <Checkbox
              name="noDNS"
              label="I don't have a DNS provider"
              className="pl-4 pb-2"
              disabled={isSubmitting}
            />

            <MultiSelect
              name="dhcp"
              id="selectDhcp"
              placeholder="Choose a DHCP"
              options={LOG_SOURCE_DHCP_OPTIONS}
              label="DHCP"
              isDisabled={values.noDHCP || isSubmitting}
              required
            />

            <Checkbox
              name="noDHCP"
              label="I don't have a DHCP server"
              className="pl-4 pb-2"
              disabled={isSubmitting}
            />

            <MultiSelect
              name="firewalls"
              id="selectFirewalls"
              placeholder="Choose a firewall"
              options={FIREWALLS_OPTIONS}
              label="Firewalls"
              isDisabled={values.noFirewalls || isSubmitting}
              required
            />

            <Checkbox
              name="noFirewalls"
              label="I don't have any firewalls"
              className="pl-4 pb-2"
              disabled={isSubmitting}
            />

            <MultiSelect
              name="switches"
              id="selectSwitches"
              placeholder="Choose a switch"
              options={SWITCHES_OPTIONS}
              label="Switches"
              className="mb-4"
              isDisabled={isSubmitting}
            />

            <MultiSelect
              name="wap"
              id="selectWap"
              placeholder="Choose a wireless access point"
              options={WIRELESS_OPTIONS}
              label="Wireless Access Points"
              isDisabled={isSubmitting}
              className="mb-4"
            />

            <MultiSelect
              name="antivirus"
              id="selectAntivirus"
              placeholder="Choose an endpoint security software"
              options={LOG_SOURCE_ANTIVIRUS_OPTIONS}
              label="Endpoint Security Software"
              className="mb-4"
              isDisabled={isSubmitting}
            />

            <MultiSelect
              name="ids"
              id="selectIds"
              placeholder="Choose an IDS/IPS"
              options={LOG_SOURCE_IDS_OPTIONS}
              label="Intrusion Detection/Prevention Systems (IDS/IPS)"
              className="mb-4"
              isDisabled={isSubmitting}
            />

            <TextArea
              name="vpn"
              className="mt-1"
              label="Client VPN services"
              disabled={isSubmitting}
            />

            <SingleSelect
              name="proxy"
              id="selectProxy"
              placeholder="Choose a proxy"
              label="Proxies"
              options={LOG_SOURCE_PROXY_OPTIONS}
              className="mb-4"
              isDisabled={isSubmitting}
            />

            <TextArea
              name="cls"
              className="mt-1"
              label="Custom Log Sources"
              disabled={isSubmitting}
            />
            <TextArea
              name="notes"
              className="mt-1"
              label="Notes"
              disabled={isSubmitting}
            />
          </div>
        </div>
      </form>
    </OnboardingModal>
  );
};

const mapPropsToValues = ({ site }: FormikOuterProps): SiteFormValues => {
  if (site) {
    const { address, ...restSite } = site;
    const { id: addressId, ...restAddr } = address;
    const noFirewalls = site.firewalls.length === 0,
      noDHCP =
        site.dhcp.length === 0 ||
        (site.dhcp.length === 1 && site.dhcp[0] === 'None'), // backwards compatibility
      noDNS =
        site.dns.length === 0 ||
        (site.dns.length === 1 && site.dns[0] === 'None'), // backwards compatibility
      thirdPartySite = site.thirdPartySite ?? false;
    return {
      ...restAddr,
      ...restSite,
      noFirewalls,
      noDHCP,
      noDNS,
      thirdPartySite,
      addressId,
    };
  }
  return EMPTY_SITE_FORM_DATA;
};

const handleSubmit = (
  values: SiteFormValues,
  formikBag: FormikBag<FormikOuterProps, SiteFormValues>,
) => {
  const {
    props: { onSave, onClose },
    resetForm,
    setSubmitting,
  } = formikBag;

  const callbacks = {
    onSuccess: () => {
      onClose();
      resetForm();
    },
    onError: () => {
      setSubmitting(false);
    },
  };

  onSave(values, callbacks);
};

const FormikModal = withFormik<FormikOuterProps, SiteFormValues>({
  enableReinitialize: true,
  mapPropsToValues,
  handleSubmit,
  validationSchema: ({ siteNames, site }: StoreProps) => {
    return createSiteValidationSchema(siteNames, site?.name);
  },
  validateOnMount: true,
})(OnboardingSiteModal);

const formValuesToSite = (values: SiteFormValues): OBSite => {
  const {
    noFirewalls,
    noDNS,
    noDHCP,
    firewalls,
    dns,
    dhcp,
    // isolatedWireless,
    // wap,

    addressId,
    country,
    street,
    street2,
    city,
    region,
    postcode,

    ...rest
  } = values;

  return {
    ...rest,
    firewalls: noFirewalls ? [] : firewalls,
    dns: noDNS ? [] : dns,
    dhcp: noDHCP ? [] : dhcp,
    // wap: siteModal.isolatedWireless ? [] : wap,
    address: {
      id: addressId,
      country,
      street,
      street2,
      city,
      region,
      postcode,
    },
  };
};

export default connect<StoreProps, DispatchProps, OwnProps>(
  (store, { currentId }): StoreProps => ({
    site: onboardingSelector.getSiteByUuid(currentId)(store),
    siteNames: onboardingSelector.getSiteNames(store),
  }),
  (dispatch: Dispatch, { currentId: uuid }): DispatchProps => ({
    onSave: (values, callbacks) => {
      const site = formValuesToSite(values);
      uuid
        ? dispatch(apiRequest.updateOBSite(uuid, site, callbacks))
        : dispatch(apiRequest.createOBSite(site, callbacks));
    },
  }),
)(FormikModal);
